import {
    useEffect,
    useState
} from "react";
import {
    TextInput as NativeTextInput,
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    Organization
} from "./views/organizationFormModal/type";
import stylesheet from "./stylesheet";
import OrganizationFormModal from "./views/organizationFormModal";
import OrganizationPreviewModal from "./views/organizationPreviewModal";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitToast,
    NCoreUIKitModal,
    NCoreUIKitTheme,
    PageContainer,
    StateCard,
    TextInput,
    Button,
    Avatar,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    ComponentIcon,
    PencilIcon,
    TrashIcon,
    PlusIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";

const RootOrganizations = () => {
    const [
        organizations,
        setOrganizations
    ] = useState<Organization[]>([]);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState<string>("");

    const [
        jumpPage,
        setJumpPage
    ] = useState<string>("");

    const [
        search,
        setSearch
    ] = useState<string>("");

    const [
        totalPages,
        setTotalPages
    ] = useState<number>(1);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        width
    } = useWindowDimensions();

    const isMobile = width < 768;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [search]);

    const fetchOrganizations = async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const response = await fetch(`${process.env.API_URL}/organizations?page=${page}&limit=10&search=${encodeURIComponent(debouncedSearch)}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                method: "GET"
            });
            const data = await response.json();
            if (data.status === "success") {
                setOrganizations(data.data);
                if (data.meta && data.meta.totalPages) {
                    setTotalPages(data.meta.totalPages);
                } else {
                    setTotalPages(1);
                }
            } else {
                NCoreUIKitToast.open({
                    title: data.message || localize("org-toast-fetch-failed")
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("org-toast-server-error")
            });
        }
    };

    useEffect(() => {
        fetchOrganizations();
    }, [
        page,
        debouncedSearch
    ]);

    const openFormModal = (org?: Organization) => {
        const modalId = "org-form-modal";
        NCoreUIKitModal.open({
            children: (
                <OrganizationFormModal
                    onSuccess={fetchOrganizations}
                    modalId={modalId}
                    initialData={org}
                />),
            onOverlayPress: ({
                closeAnimation
            }) => closeAnimation(),
            id: modalId
        });
    };

    const openPreviewModal = (org: Organization) => {
        const modalId = "org-preview-modal";
        NCoreUIKitModal.open({
            children: (
                <OrganizationPreviewModal
                    modalId={modalId}
                    org={org}
                />),
            onOverlayPress: ({
                closeAnimation
            }) => closeAnimation(),
            id: modalId
        });
    };

    const handleDelete = (id: string, name: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const response = await fetch(`${process.env.API_URL}/organizations/${id}`, {
                            headers: {
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });
                        const data = await response.json();
                        if (data.status === "success") {
                            NCoreUIKitToast.open({
                                title: localize("org-toast-delete-success")
                            });
                            fetchOrganizations();
                        } else {
                            NCoreUIKitToast.open({
                                title: data.message || localize("org-toast-delete-error")
                            });
                        }
                    } catch {
                        NCoreUIKitToast.open({
                            title: localize("org-toast-server-error")
                        });
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("org-dialog-delete-primary"),
                color: "error"
            },
            content: localize("org-dialog-delete-content", [
                name
            ]),
            secondaryButtonProps: {
                title: localize("org-dialog-delete-cancel"),
                color: "background-grey",
                onPress: ({
                    closeAnimation
                }) => {
                    closeAnimation();
                }
            },
            title: localize("org-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.header,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("org-page-title")}
                </Text>
                <View
                    style={[
                        stylesheet.headerButtonsContainer,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchOrganizations();
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <PlusIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("org-page-add-button")}
                        onPress={() => openFormModal()}
                        color="primary"
                    />
                </View>
            </View>
            <View
                style={[
                    stylesheet.searchContainer,
                    {
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("org-search-placeholder")}
                    spreadBehaviour="stretch"
                    onChangeText={setSearch}
                    value={search}
                />
            </View>
            <View
                style={[
                    stylesheet.listContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <ScrollView
                    contentContainerStyle={[
                        stylesheet.scrollContent,
                        {
                            paddingBottom: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                    showsVerticalScrollIndicator={false}
                >
                    {
                        organizations.length === 0 ?
                            <StateCard
                                subTitle={debouncedSearch ? localize("org-list-search-empty-subtitle") : localize("org-list-empty-subtitle")}
                                title={debouncedSearch ? localize("org-list-search-empty-title") : localize("org-list-empty-title")}
                                icon={({
                                    color
                                }) => <ComponentIcon
                                    color={color as never}
                                />
                                }
                                type="neutral"
                            />
                        :
                            (
                                organizations.map((org) => {
                                    return <TouchableOpacity
                                        style={[
                                            stylesheet.orgCard,
                                            {
                                                backgroundColor: colors.content.container.mid,
                                                alignItems: isMobile ? "stretch" : "center",
                                                flexDirection: isMobile ? "column" : "row",
                                                borderRadius: radiuses.md,
                                                padding: spaces.spacingMd,
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                        onPress={() => openPreviewModal(org)}
                                        activeOpacity={0.7}
                                        key={org.id}
                                    >
                                        <View
                                            style={[
                                                stylesheet.cardContent,
                                                {
                                                    gap: spaces.spacingMd
                                                }
                                            ]}
                                        >
                                            <Avatar
                                                imageUrl={org.profilePicturePath || ""}
                                                title={org.name}
                                                size="small"
                                            />
                                            <Text
                                                variant="bodyLargeSize"
                                            >
                                                {org.name}
                                            </Text>
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.cardActions,
                                                {
                                                    flexDirection: isMobile ? "column" : "row",
                                                    gap: spaces.spacingMd
                                                }
                                            ]}
                                        >
                                            <Button
                                                icon={({
                                                    color,
                                                    size
                                                }) => <PencilIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />
                                                }
                                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                                title={localize("org-list-edit-button")}
                                                onPress={() => openFormModal(org)}
                                                type="neutral"
                                            />
                                            <Button
                                                icon={({
                                                    color,
                                                    size
                                                }) => <TrashIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />
                                                }
                                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                                onPress={() => handleDelete(org.id, org.name)}
                                                title={localize("org-list-delete-button")}
                                                type="danger"
                                            />
                                        </View>
                                    </TouchableOpacity>;
                                })
                            )
                    }
                </ScrollView>
                {
                    totalPages > 1 && (
                        <View
                            style={[
                                stylesheet.paginationContainer,
                                {
                                    gap: isMobile ? spaces.spacingMd : spaces.spacingXl,
                                    flexDirection: isMobile ? "column" : "row",
                                    paddingTop: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={{
                                    justifyContent: isMobile ? "space-between" : "center",
                                    gap: isMobile ? spaces.spacingMd : spaces.spacingXl,
                                    width: isMobile ? "100%" : undefined,
                                    alignItems: "center",
                                    flexDirection: "row"
                                }}
                            >
                                <Button
                                    icon={({
                                        color,
                                        size
                                    }) => <ChevronLeftIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />
                                    }
                                    onPress={() => setPage(p => Math.max(1, p - 1))}
                                    title={localize("org-pagination-prev")}
                                    isDisabled={page === 1}
                                    color="secondary"
                                />
                                <Text
                                    variant="bodyMediumSize"
                                >
                                    {page} / {totalPages}
                                </Text>
                                <Button
                                    icon={({
                                        color,
                                        size
                                    }) => <ChevronRightIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />
                                    }
                                    onPress={() => setPage(p => Math.min(totalPages, p + 1))}
                                    title={localize("org-pagination-next")}
                                    isDisabled={page === totalPages}
                                    iconDirection="right"
                                    color="secondary"
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.jumpContainer,
                                    {
                                        marginLeft: isMobile ? 0 : spaces.spacingMd,
                                        alignItems: isMobile ? "stretch" : "center",
                                        flexDirection: isMobile ? "column" : "row",
                                        width: isMobile ? "100%" : undefined,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.jumpInputContainer,
                                        {
                                            width: isMobile ? "100%" : undefined
                                        }
                                    ]}
                                >
                                    <NativeTextInput
                                        onChangeText={(text) => setJumpPage(text.replace(/[^0-9]/g, ""))}
                                        placeholder={localize("org-pagination-jump-placeholder")}
                                        style={[
                                            stylesheet.jumpInput,
                                            {
                                                borderColor: colors.content.border.subtle,
                                                paddingHorizontal: spaces.spacingMd,
                                                color: colors.content.text.high,
                                                width: isMobile ? "100%" : 60,
                                                borderRadius: radiuses.md
                                            }
                                        ]}
                                        placeholderTextColor={colors.content.text.low}
                                        keyboardType="numeric"
                                        value={jumpPage}
                                    />
                                </View>
                                <Button
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    title={localize("org-pagination-jump-button")}
                                    onPress={() => {
                                        const p = parseInt(jumpPage, 10);
                                        if (p >= 1 && p <= totalPages) {
                                            setPage(p);
                                        }
                                        setJumpPage("");
                                    }}
                                    isDisabled={!jumpPage}
                                    color="secondary"
                                />
                            </View>
                        </View>)
                }
            </View>
        </View>
    </PageContainer>;
};

export default RootOrganizations;
